import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Banknote, BookOpen, LayoutDashboard, LifeBuoy, Megaphone, Menu, Search, ShieldCheck, Users, X } from "lucide-react";

import { api } from "@/core/api/client";
import { reportCurrentDevice } from "@/core/device/report";
import { useT } from "@/core/i18n";
import { useSession } from "@/core/state/session";
import type { MessageKey } from "@/core/i18n/types";
import { LegalFooter } from "@/shared/legal/LegalFooter";
import { canReadIntegrationDocs } from "@/features/docs/visibility";
import { LegalReadPrompt } from "@/features/legal/LegalReadPrompt";
import { CommandPalette } from "@/shared/ui/CommandPalette";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { NoticeButton } from "./NoticeButton";
import { ProfileMenu } from "./ProfileMenu";

type NavItem = { to: string; labelKey: MessageKey; icon: typeof LayoutDashboard; end?: boolean };

const personalNav: NavItem[] = [
  { to: "/", labelKey: "common.nav.overview", icon: LayoutDashboard, end: true },
  { to: "/account", labelKey: "common.nav.account", icon: ShieldCheck },
  { to: "/apps", labelKey: "common.nav.apps", icon: Users },
  { to: "/security", labelKey: "common.nav.security", icon: ShieldCheck },
];

const inboxNav: NavItem[] = [
  { to: "/support", labelKey: "common.nav.support", icon: LifeBuoy },
  { to: "/announcements", labelKey: "common.nav.announcements", icon: Megaphone },
];

export function AppShell() {
  const [open, setOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const t = useT();
  const identity = useSession((state) => state.identity);
  const managementTenant = useSession((state) => state.managementTenantSlug);
  const setManagementTenant = useSession((state) => state.setManagementTenant);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = identity?.roles.includes("tenant_admin") || identity?.roles.includes("super_admin");
  const isSuper = identity?.roles.includes("super_admin");
  const isDeveloper = identity?.roles.includes("oidc_developer") || isSuper;
  const isWithdrawal = identity?.roles.includes("tenant_withdrawal_initiator") || isSuper;
  const canReadDocs = canReadIntegrationDocs(identity);
  const tenants = useQuery({ queryKey: ["tenants", "workspace-switcher"], queryFn: () => api.tenants() as Promise<{ items: Array<Record<string, unknown>> }>, enabled: Boolean(isSuper), staleTime: 30_000 });

  useEffect(() => {
    function onMaintenance(event: Event) {
      const detail = (event as CustomEvent<string>).detail;
      setMaintenanceMessage(detail || t("apiErrors.maintenance"));
    }
    window.addEventListener("owo:maintenance", onMaintenance);
    return () => window.removeEventListener("owo:maintenance", onMaintenance);
  }, [t]);

  useEffect(() => {
    if (!identity?.tenantSlug) return;
    void reportCurrentDevice(identity.tenantSlug).catch(() => undefined);
  }, [identity?.tenantSlug, identity?.userId]);

  useEffect(() => {
    const root = document.getElementById("main-content");
    if (!root) return;
    const host: HTMLElement = root;
    const narrow = window.matchMedia("(max-width: 760px)");
    const cleanups: Array<() => void> = [];
    function revealActiveTab() {
      host.querySelectorAll(".workspace-tabs .ant-tabs-tab-active").forEach((tab) => {
        const wrap = tab.closest(".ant-tabs-nav-wrap");
        if (!(wrap instanceof HTMLElement)) return;
        const tabBox = tab.getBoundingClientRect();
        const wrapBox = wrap.getBoundingClientRect();
        if (tabBox.left < wrapBox.left + 4 || tabBox.right > wrapBox.right - 4) {
          tab.scrollIntoView({ inline: "nearest", block: "nearest" });
        }
      });
    }
    function bindScroller(scroller: HTMLElement) {
      if (scroller.dataset.hScroll === "1") return;
      scroller.dataset.hScroll = "1";
      const controls = document.createElement("div");
      controls.className = "h-scroll-controls";
      const prev = document.createElement("button");
      const next = document.createElement("button");
      prev.type = "button";
      next.type = "button";
      prev.className = "h-scroll-btn";
      next.className = "h-scroll-btn";
      prev.setAttribute("aria-label", t("shell.scrollBack"));
      next.setAttribute("aria-label", t("shell.scrollForward"));
      prev.textContent = "‹";
      next.textContent = "›";
      function move(direction: number) {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        scroller.scrollBy({ left: direction * Math.max(120, scroller.clientWidth * 0.7), behavior: reduce ? "auto" : "smooth" });
      }
      prev.addEventListener("click", () => move(-1));
      next.addEventListener("click", () => move(1));
      controls.append(prev, next);
      scroller.insertAdjacentElement("afterend", controls);
      function update() {
        const overflow = scroller.scrollWidth > scroller.clientWidth + 1;
        controls.hidden = !overflow;
        prev.disabled = scroller.scrollLeft <= 1;
        next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      }
      scroller.addEventListener("scroll", update, { passive: true });
      const resize = new ResizeObserver(update);
      resize.observe(scroller);
      update();
      cleanups.push(() => {
        scroller.removeEventListener("scroll", update);
        resize.disconnect();
        delete scroller.dataset.hScroll;
        controls.remove();
      });
    }
    function bindRegions() {
      host.querySelectorAll(".workspace-tabs > .ant-tabs-nav").forEach((nav) => {
        const wrap = nav.querySelector(":scope > .ant-tabs-nav-wrap");
        if (wrap instanceof HTMLElement) bindScroller(wrap);
      });
      host.querySelectorAll(".ant-table-wrapper, .h-scroll").forEach((node) => {
        if (node instanceof HTMLElement) bindScroller(node);
      });
    }
    function release() {
      cleanups.forEach((cleanup) => cleanup());
      cleanups.length = 0;
    }
    function scan() {
      if (!narrow.matches) {
        release();
        return;
      }
      bindRegions();
      revealActiveTab();
    }
    scan();
    const observer = new MutationObserver((records) => {
      if (!narrow.matches) return;
      const added = records.some((record) => record.type === "childList");
      const selected = records.some((record) => record.type === "attributes" && record.attributeName === "aria-selected");
      if (added) bindRegions();
      if (added || selected) revealActiveTab();
    });
    observer.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-selected"] });
    narrow.addEventListener("change", scan);
    return () => {
      observer.disconnect();
      narrow.removeEventListener("change", scan);
      release();
    };
  }, [location.pathname, location.search, t]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((value) => !value);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function switchWorkspace(slug: string) {
    if (slug === managementTenant && location.pathname === "/admin") return;
    setManagementTenant(slug);
    setOpen(false);
    navigate("/admin");
  }

  const navGroups: Array<{ id: string; labelKey: MessageKey; items: NavItem[] }> = [
    { id: "personal", labelKey: "shell.navPersonal", items: personalNav },
    { id: "inbox", labelKey: "shell.navInbox", items: inboxNav },
    { id: "build", labelKey: "shell.navBuild", items: [
      ...(canReadDocs ? [{ to: "/docs", labelKey: "common.nav.docs" as const, icon: BookOpen }] : []),
      ...(isDeveloper ? [{ to: "/developer", labelKey: "common.nav.developer" as const, icon: BookOpen }] : []),
    ] },
    { id: "payout", labelKey: "shell.navPayout", items: isWithdrawal ? [{ to: "/withdrawal", labelKey: "common.nav.withdrawal", icon: Banknote }] : [] },
    { id: "manage", labelKey: "shell.navManage", items: [
      ...(isAdmin ? [{ to: "/admin", labelKey: "common.nav.admin" as const, icon: Users }] : []),
      ...(isSuper ? [{ to: "/platform", labelKey: "common.nav.platform" as const, icon: ShieldCheck }] : []),
    ] },
  ];

  return <><LegalReadPrompt /><div className="app-frame">
    <aside className={`side-rail ${open ? "is-open" : ""}`}>
      <div className="brand-lockup"><span className="brand-mark">OwO!</span><span className="brand-product">Identity</span></div>
      <div className="rail-context"><span className="context-label">{isSuper ? t("shell.workspaceAdmin") : t("shell.workspaceCurrent")}</span>{isSuper ? <Select aria-label={t("shell.workspaceAdmin")} className="workspace-switcher" value={managementTenant ?? identity?.tenantSlug} loading={tenants.isLoading} showSearch optionFilterProp="label" onChange={switchWorkspace} options={(tenants.data?.items ?? []).filter((tenant) => tenant.is_active).map((tenant) => ({ value: String(tenant.slug), label: String(tenant.display_name ?? tenant.name ?? tenant.slug) }))} /> : <strong>{identity?.tenantSlug ?? t("shell.noTenant")}</strong>}<span className="context-role">{isSuper ? t("shell.roleSuper", { tenant: identity?.tenantSlug ?? "" }) : isAdmin ? t("shell.roleAdmin") : t("shell.roleMember")}</span></div>
      <nav className="primary-nav" aria-label={t("shell.navLabel")}>{navGroups.filter((group) => group.items.length > 0).map((group) => <section key={group.id} className="nav-group" aria-labelledby={`nav-group-${group.id}`}><h2 id={`nav-group-${group.id}`} className="nav-group-label">{t(group.labelKey)}</h2><div className="nav-group-links">{group.items.map(({ to, labelKey, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}><Icon size={17} strokeWidth={1.8} /><span>{t(labelKey)}</span></NavLink>)}</div></section>)}</nav>
    </aside>
    {open && <button className="scrim" aria-label={t("shell.closeNav")} onClick={() => setOpen(false)} />}
    <main className="main-surface" id="main-content"><a className="skip-link" href="#main-content">{t("shell.skip")}</a><header className="topbar"><button className="icon-button mobile-menu" onClick={() => setOpen(!open)} aria-label={t("shell.openNav")}>{open ? <X size={20} /> : <Menu size={20} />}</button><div className="breadcrumb"><span>OwO! Identity</span><b>/</b><span>{isSuper && location.pathname.startsWith("/admin") ? managementTenant : identity?.tenantSlug ?? t("shell.workspaceFallback")}</span></div><div className="topbar-actions"><button className="icon-button" aria-label={t("shell.commandPalette")} title={t("shell.commandHint")} onClick={() => setCommandOpen(true)}><Search size={18} /></button><NoticeButton /><ProfileMenu /></div></header><div className="page-content">{maintenanceMessage && !isSuper ? <section className="maintenance-hold" role="status"><h1>{t("maintenance.holdTitle")}</h1><p>{maintenanceMessage}</p><p>{t("maintenance.holdHint")}</p></section> : <><AnnouncementBanner /><Outlet /></>}</div><LegalFooter /></main>
    <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
  </div></>;
}
